import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { courseGoals } from '@/mocks/curriculumData';

const LEVEL_COUNT = 3;
const PERSPECTIVE_COUNT = 5;

const barColors = [
  'bg-accent-500/30',
  'bg-accent-500/50',
  'bg-accent-500/80',
];

const textColors = [
  'text-accent-700',
  'text-accent-600',
  'text-accent-500',
];

export default function CourseGoals() {
  const { t } = useTranslation();

  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          {String(t('curriculum.goals.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('curriculum.goals.title')}
          </h2>
          {String(t('curriculum.goals.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto"></div>
          {String(t('curriculum.goals.description')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed max-w-[650px] mx-auto mt-5">{__s}</p>))}
        </div>

        {/* Desktop: Table view */}
        <div className="hidden md:block overflow-hidden border border-background-200/80">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-primary-900">
                  <th className="w-[220px] px-6 py-4 text-left text-[14px] text-accent-400 tracking-[0.1em] font-medium font-heading">
                    {t('curriculum.goals.tableHeader')}
                  </th>
                  {Array.from({ length: LEVEL_COUNT }, (_, lIdx) => (
                    <th key={lIdx} className="px-6 py-4 text-center text-[14px] text-accent-400 tracking-[0.1em] font-medium font-heading">
                      <div>{t(`curriculum.goals.level.${lIdx}.course`)}</div>
                      <div className="text-[9px] text-accent-500/50 tracking-[0.12em] mt-0.5">{t(`curriculum.goals.level.${lIdx}.courseEn`)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: PERSPECTIVE_COUNT }, (_, pIdx) => (
                  <tr
                    key={pIdx}
                    className={`border-t border-background-200/60 ${
                      pIdx % 2 === 0 ? 'bg-background-100/50' : 'bg-background-50'
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="text-[15px] text-foreground-800 tracking-[0.04em] font-medium">
                        {t(`curriculum.goals.perspective.${pIdx}.label`)}
                      </div>
                      <div className="text-[12px] text-foreground-400 tracking-[0.03em] mt-0.5">
                        {t(`curriculum.goals.perspective.${pIdx}.desc`)}
                      </div>
                    </td>
                    {Array.from({ length: LEVEL_COUNT }, (_, lIdx) => (
                      <td key={lIdx} className="px-4 py-4 text-center">
                        <div className="inline-flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-background-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-700 ${barColors[lIdx]}`}
                              style={{ width: `${courseGoals.levels[lIdx].goals[pIdx]}%` }}
                            ></div>
                          </div>
                          <span className={`text-[15px] font-medium tracking-[0.04em] ${textColors[lIdx]}`}>
                            {courseGoals.levels[lIdx].goals[pIdx]}%
                          </span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile: Card view */}
        <div className="md:hidden space-y-8">
          {Array.from({ length: LEVEL_COUNT }, (_, lIdx) => (
            <div key={lIdx} className="border border-background-200/60 bg-background-50 p-5">
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-background-200/50">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-900 text-accent-400">
                  <i className={`${courseGoals.levels[lIdx].icon} text-sm`}></i>
                </div>
                <div>
                  <h4 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em]">{t(`curriculum.goals.level.${lIdx}.course`)}</h4>
                  <p className="text-[9px] text-accent-500/70 tracking-[0.1em]">{t(`curriculum.goals.level.${lIdx}.courseEn`)}</p>
                </div>
              </div>
              <div className="space-y-3">
                {Array.from({ length: PERSPECTIVE_COUNT }, (_, pIdx) => (
                  <div key={pIdx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[14px] text-foreground-600 tracking-[0.04em]">{t(`curriculum.goals.perspective.${pIdx}.label`)}</span>
                      <span className={`text-[15px] font-medium tracking-[0.04em] ${textColors[lIdx]}`}>
                        {courseGoals.levels[lIdx].goals[pIdx]}%
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${barColors[lIdx]}`}
                        style={{ width: `${courseGoals.levels[lIdx].goals[pIdx]}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        {String(t('curriculum.goals.note')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-400 tracking-[0.04em] mt-6 text-center">{__s}</p>))}

        {/* Course link */}
        <div className="text-center mt-6">
          <Link
            to="/courses"
            className="inline-block px-8 py-3 border border-accent-500/30 text-accent-600 text-[15px] tracking-[0.08em] hover:bg-accent-500/5 hover:border-accent-400/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('curriculum.goals.coursesLink')}
          </Link>
        </div>
      </div>
    </section>
  );
}
